import {
  AnalysisEngineType,
  AnalysisErrorReason,
  AnalysisState,
  RecordedData,
} from "../types";
import { detectVolumeAtom, recordingSettingAtom } from "../store";
import { MutableRefObject, useCallback } from "react";
import { useGetAnalysisEngines } from "../utils/selectAnalysisEngine";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import { USE_ANALYSIS_ERROR_PAGE } from "../environments";
import { useToastMessage } from "./useToastMessage";
import axios from "axios";
import { generateCoreVoiceFileData } from "../utils/generateVoiceFileData";

type HandleApiError = (err: unknown) => void;

function useHandleApiError(): HandleApiError {
  const navigate = useNavigate();
  const toastMessage = useToastMessage();
  return useCallback(
    (err: unknown): void => {
      if (
        USE_ANALYSIS_ERROR_PAGE &&
        axios.isAxiosError(err) &&
        err.code &&
        err.message
      ) {
        const state: AnalysisErrorReason = {
          code: err.code,
          message: err.message,
        };
        navigate(`../analysis-error`, { state });
      } else {
        toastMessage(err);
      }
    },
    [navigate, toastMessage],
  );
}

function randomUnsafeStr(): string {
  // eslint-disable-next-line sonarjs/pseudo-random
  return Math.random().toString(32).substring(2);
}

type AnalyzeVoice = (
  recordedData: RecordedData[],
  requestIdRef: MutableRefObject<null | string>,
  setAnalysisStatus: (state: AnalysisState) => void,
) => Promise<void>;

export function useAnalyzeVoice(engineType: AnalysisEngineType): AnalyzeVoice {
  const handleApiError = useHandleApiError();
  const analysisEngines = useGetAnalysisEngines(engineType);
  const [detectVolume] = useAtom(detectVolumeAtom);
  const [recordingSetting] = useAtom(recordingSettingAtom);
  const navigate = useNavigate();

  return useCallback(
    async (recordedData, requestIdRef, setAnalysisStatus) => {
      setAnalysisStatus("UPLOADING");
      const requestId = randomUnsafeStr();
      requestIdRef.current = requestId;

      try {
        // ファイルアップロード
        const formData = generateCoreVoiceFileData(recordedData);
        if (recordingSetting?.setting_data?.keep_files) {
          formData.append("keep_file", "true");
        }
        const {
          data: uploadData,
          error: uploadError,
          response: uploadResponse,
        } = await voislogApi.POST("/v2/core/recordings/upload", {
          body: formData as never,
        });
        if (uploadError) {
          throw new ApiError(uploadResponse, "post", uploadError);
        }

        const analysisFileId = uploadData.recording_id;

        // 音声解析
        const requests = analysisEngines.map((engine) =>
          engine.analyze(analysisFileId, detectVolume),
        );

        setAnalysisStatus("ANALYZING");
        const analysisResponse = await Promise.allSettled(requests);

        // 解析結果確認
        const isAllFailed = analysisResponse.every(
          (result) => result.status === "rejected",
        );
        if (isAllFailed) {
          handleApiError((analysisResponse[0] as PromiseRejectedResult).reason);
        } else if (requestIdRef.current === requestId) {
          navigate(`../results/${analysisFileId}`);
        }
      } catch (err) {
        handleApiError(err);
      } finally {
        setAnalysisStatus("IDLE");
      }
    },
    [analysisEngines, detectVolume, recordingSetting, handleApiError, navigate],
  );
}
